/**
 * Keeps `storageAccountInfoStore` in step with the connected Azure Storage
 * connections: one `azure-storage:account-info` call per connect, the tree
 * rebuilt when it answers (folders omitted for unavailable services, spec
 * §5.1 (d)), the snapshot forgotten on disconnect. Mounted once by
 * `DatabaseExplorer`. Each probe's reply is fenced by the token `setLoading`
 * issued: a disconnect (or a re-probe) in between makes the store drop it, so
 * a late reply never resurrects the forgotten snapshot or overwrites the next
 * connect's own probe (review r2).
 *
 * "One per connect" means one per CONNECTION GENERATION, not per connection
 * id: the in-place Reconnect (`connectToDatabase(…, { force: true })`,
 * `DatabaseExplorer.handleReconnect`) replaces the active entry without ever
 * leaving `connected`, so an id/status comparison would find the old snapshot,
 * skip the probe and keep reporting the previous account's service
 * availability. The generation is `ActiveConnection.generation`, the monotonic
 * attempt id `sqlConnectionService.openConnection` stamps on every successful
 * connect — NOT `connectedAt`, whose millisecond `Date` two connects can share,
 * which made the second one skip its probe and keep the first one's answer
 * (review r3, tightened in r4). A differing generation re-probes; the new
 * `setLoading` token then drops the superseded attempt's reply.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { getStorageAccountInfo, toStorageOperationError } from '../../workspace/storage-explorer/services/azureStorageApi';
import { useStorageAccountInfoStore } from '../store/storageAccountInfoStore';
import { rebuildStorageConnectionChildren, setStorageTreeQueryClient } from '../services/storageConnectionTree';

export async function reprobeStorageAccountInfo(connectionId: string, generation: number): Promise<void> {
  const store = useStorageAccountInfoStore.getState();
  const token = store.setLoading(connectionId, generation);
  let applied: boolean;
  try {
    const info = await getStorageAccountInfo(connectionId);
    applied = useStorageAccountInfoStore.getState().setInfo(connectionId, info, token);
  } catch (raw) {
    applied = useStorageAccountInfoStore.getState().setError(connectionId, toStorageOperationError(raw), token);
  }
  if (applied) rebuildStorageConnectionChildren(connectionId);
}

export function useStorageAccountInfoSync(): void {
  const activeConnections = useConnectionStore((s) => s.activeConnections);
  // S7 review E: a rebuild's reload lists through the explorer's query client, never a private one.
  const queryClient = useQueryClient();
  useEffect(() => {
    setStorageTreeQueryClient(queryClient);
    return () => setStorageTreeQueryClient(undefined);
  }, [queryClient]);
  useEffect(() => {
    const store = useStorageAccountInfoStore.getState();
    const connectedStorageGenerations = new Map(
      activeConnections
        .filter((active) => active.connection.engine === 'azurestorage' && active.status === 'connected')
        .map((active) => [active.id, active.generation ?? 0] as const),
    );
    for (const id of Object.keys(store.byConnection)) {
      if (!connectedStorageGenerations.has(id)) store.forget(id);
    }
    for (const [id, generation] of connectedStorageGenerations) {
      if (store.byConnection[id]?.generation === generation) continue;
      void reprobeStorageAccountInfo(id, generation);
    }
  }, [activeConnections]);
}
